import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Day } from '@/types';

// ============================================
// Collection Path Helpers
// ============================================

const getDayRef = (userId: string, date: string) => doc(db, 'users', userId, 'days', date);

// ============================================
// Helper Functions
// ============================================

function createDefaultDay(userId: string, date: string): Omit<Day, 'id'> {
  return {
    userId,
    date,
    blockIds: [],
    ritualCompleted: false,
    ritualCompletedAt: null,
    reflection: null,
    quote: null,
    carryOverBlockIds: [],
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
}

// ============================================
// CRUD Operations
// ============================================

export async function getDay(userId: string, date: string): Promise<Day | null> {
  const dayRef = getDayRef(userId, date);
  const snapshot = await getDoc(dayRef);

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data();
  return {
    id: snapshot.id,
    userId: data.userId,
    date: data.date,
    blockIds: data.blockIds || [],
    ritualCompleted: data.ritualCompleted || false,
    ritualCompletedAt: data.ritualCompletedAt || null,
    reflection: data.reflection || null,
    quote: data.quote || null,
    carryOverBlockIds: data.carryOverBlockIds || [],
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

export async function createDay(
  userId: string,
  date: string,
  data?: Partial<Omit<Day, 'id'>>
): Promise<Day> {
  const dayRef = getDayRef(userId, date);
  const defaultDay = createDefaultDay(userId, date);
  const newDay = { ...defaultDay, ...data };

  await setDoc(dayRef, newDay);

  return {
    id: date,
    ...newDay,
  };
}

export async function updateDay(
  userId: string,
  date: string,
  updates: Partial<Day>
): Promise<void> {
  const dayRef = getDayRef(userId, date);
  await updateDoc(dayRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function ensureDayExists(userId: string, date: string): Promise<Day> {
  const existingDay = await getDay(userId, date);

  if (existingDay) {
    return existingDay;
  }

  return createDay(userId, date);
}

// ============================================
// Ritual Operations
// ============================================

export async function completeRitual(
  userId: string,
  date: string,
  reflection: string | null,
  quote: string | null,
  carryOverBlockIds: string[]
): Promise<void> {
  const dayRef = getDayRef(userId, date);
  await updateDoc(dayRef, {
    ritualCompleted: true,
    ritualCompletedAt: serverTimestamp(),
    reflection,
    quote,
    carryOverBlockIds,
    updatedAt: serverTimestamp(),
  });
}

// ============================================
// Subscriptions
// ============================================

export function subscribeToDay(
  userId: string,
  date: string,
  onData: (day: Day | null) => void,
  onError?: (error: Error) => void
): () => void {
  const dayRef = getDayRef(userId, date);

  return onSnapshot(
    dayRef,
    (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const day: Day = {
          id: snapshot.id,
          userId: data.userId,
          date: data.date,
          blockIds: data.blockIds || [],
          ritualCompleted: data.ritualCompleted || false,
          ritualCompletedAt: data.ritualCompletedAt || null,
          reflection: data.reflection || null,
          quote: data.quote || null,
          carryOverBlockIds: data.carryOverBlockIds || [],
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
        };
        onData(day);
      } else {
        onData(null);
      }
    },
    (error) => {
      console.error('Error listening to day:', error);
      onError?.(error);
    }
  );
}
